import {
    CirclePlus,
    Copy,
    Eye,
    MoreHorizontal,
    Pencil,
    Presentation,
    Trash2,
} from 'lucide-react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { useCompletion } from '@ai-sdk/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueries, useQueryClient } from 'react-query';
import { v4 as uuid } from 'uuid';
import {
    seedWidgetResult,
    useDeleteReport,
    useDuplicateReport,
    useReport,
    widgetQuery,
} from '@/utils/hooks/reports';
import { useAssistantContext } from 'components/AiAssistant';
import SlideshowExportDialog from 'components/AiAssistant/SlideshowExportDialog';
import {
    trackReportDeleted,
    trackReportRenamed,
    trackReportReordered,
    trackReportViewed,
    trackWidgetAdded,
    trackWidgetDeleted,
    trackWidgetEdited,
    trackWidgetRenderToggled,
    trackWidgetRowExcluded,
} from './reportAnalytics';
import {
    buildSynthesisSummary,
    computeReportDataHash,
    isAuthoritative,
} from './reportHash';
import {
    excludeRowFromArgs,
    pruneRowFromOutput,
    withListView,
} from '@/ui/components/AiAssistant/visuals';
import { Button } from '@/ui/design-system/components/ui/button';
import { PagePadding } from 'components/PagePadding';
import PendingWidgetBuild from './PendingWidgetBuild';
import ReportAddWidget from './ReportAddWidget';
import ReportSummary from './ReportSummary';
import ReportWidget from './ReportWidget';
import ReportWidgetGrid from './ReportWidgetGrid';
import WidgetEditModal from './WidgetEditModal';
import { SidebarTrigger } from '@/ui/design-system/components/ui/sidebar';
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
    Separator,
    SpateAiIcon,
    Text,
} from '@/ui/design-system/components';
import useReportWidgets from './useReportWidgets';
import useReportSaver from './useReportSaver';
import ReportConflictDialog from './ReportConflictDialog';
import { AI_SERVICE_URL } from '@/config/constants';

function ReportView() {
    const { reportId } = useParams();
    const {
        data: report,
        isLoading: reportLoading,
        isError,
    } = useReport(reportId);
    const deleteReport = useDeleteReport();
    const duplicateReport = useDuplicateReport();
    const history = useHistory();
    const qc = useQueryClient();
    const { togglePanel } = useAssistantContext();

    // All persisted edits (widgets, rename, synthesis) flow through one saver:
    // debounced single-flight autosave with optimistic updates + version-based
    // conflict detection. `save` takes an updater so concurrent partial saves
    // (e.g. a synthesis write landing during a widget edit) accumulate rather
    // than clobber.
    const { save, conflict, dismissConflict, reloadFromServer } =
        useReportSaver(reportId, report?.version);

    // Always start in edit mode; "Preview mode" in the toolbar's overflow menu
    // toggles back to a clean, chrome-free read view.
    const [isEditing, setIsEditing] = useState(true);
    // The widget currently open in the edit modal (null = closed). Held here, not
    // in the draggable, so the modal lives outside the drag/grid DOM and a reorder
    // never unmounts it.
    const [editingWidget, setEditingWidget] = useState(null);
    // PowerPoint export dialog (generates + downloads the .pptx directly).
    const [exportOpen, setExportOpen] = useState(false);

    // In-flight "add a widget" builds, each rendered as its own PendingWidgetBuild
    // tile so several can run concurrently while the add panel stays usable. Each
    // entry is { id, text }; on settle the build appends its widget(s) and removes
    // itself, or stays as a dismissable hint if it produced nothing.
    const [pendingBuilds, setPendingBuilds] = useState([]);
    const startBuild = (text) =>
        setPendingBuilds((prev) => [...prev, { id: uuid(), text }]);
    const dismissBuild = (buildId) =>
        setPendingBuilds((prev) => prev.filter((b) => b.id !== buildId));

    // The editable widget list (working-copy order + reseed + persist) lives in a
    // dedicated hook so this view stays focused on fetch + synthesis + render.
    const {
        widgets,
        handleMove,
        handleDropPersist,
        handleRemove,
        handleAdd,
        handleUpdate,
        handleDuplicate,
    } = useReportWidgets({ report, isEditing, save });

    // Fire one "viewed" event per report loaded. Guard on the id (not the report
    // object, which is replaced on every optimistic edit) so edits don't re-fire
    // it; navigating between reports reuses this component (React Router), so the
    // ref re-arms when the id changes. Count comes from the persisted config —
    // the working-copy `widgets` seeds empty on the first render. (Creating a
    // report navigates here, so a Created event is followed by a Viewed.)
    const viewedIdRef = useRef(null);
    useEffect(() => {
        if (!report?.id || viewedIdRef.current === report.id) return;
        viewedIdRef.current = report.id;
        trackReportViewed({
            reportId: report.id,
            widgetCount: report.config?.widgets?.length ?? 0,
        });
    }, [report]);

    // The report owns every widget fetch (one useQueries), so it has all outputs
    // to hash + synthesize. Keyed by args → reorder reuses cache, no refetch.
    const widgetQueries = useQueries(widgets.map((w) => widgetQuery(w.args)));

    const allSettled =
        widgets.length > 0 &&
        widgetQueries.every((q) => isAuthoritative(q.data) || q.isError);

    const entries = widgets.map((w, i) => ({
        args: w.args,
        // Only when set, so existing untitled reports keep their stored hash
        // (adding the key unconditionally would mass re-synthesize them all).
        ...(w.title ? { title: w.title } : {}),
        data: widgetQueries[i]?.isSuccess ? widgetQueries[i].data : null,
    }));

    // Recompute the data hash when a query's status/freshness changes, a
    // widget's args do (an edited config can resolve from an already-cached
    // query with unchanged status/freshness), or a widget title does (a
    // rename is analysis input but touches no query).
    const settleSignal = widgetQueries
        .map((q) => `${q.status}:${q.dataUpdatedAt}`)
        .join('|');
    const argsSignal = JSON.stringify(widgets.map((w) => w.args));
    const titleSignal = JSON.stringify(widgets.map((w) => w.title ?? ''));
    const dataHash = useMemo(
        () => (allSettled ? computeReportDataHash(entries) : null),

        [allSettled, settleSignal, argsSignal, titleSignal],
    );
    const storedHash = report?.config?.analysis?.dataHash ?? null;

    // ---- Synthesis (loop-safe): one analysis stream per distinct data hash ----
    // useCompletion owns the transport (fetch, plain-text streaming, abort); the
    // effect below only decides WHEN to start one, and onFinish persists the
    // result. `completion`/`isLoading` drive the rendered text + spinner. The
    // hash being synthesized is held in a ref so onFinish can stamp it. Auth
    // rides the global fetch interceptor (as with executeWidget/nameReport).
    const lastSynthHashRef = useRef(null);
    const {
        completion: streamingAnalysis,
        complete,
        stop,
        isLoading: isSynthesizing,
        error: synthError,
    } = useCompletion({
        api: `${AI_SERVICE_URL}/ai/reports/synthesize/`,
        streamProtocol: 'text',
        experimental_throttle: 50,
        onFinish: (_prompt, analysis) => {
            if (!report || !analysis) return;
            // Write analysis as an updater on the saver's latest config, never a
            // bare { analysis }: a synthesis stream runs for seconds and often
            // overlaps a widget add, so building on `prev` preserves the
            // just-added widgets (and the create-time title) instead of
            // clobbering them.
            save((prev) => ({
                ...prev,
                analysis: {
                    text: analysis,
                    dataHash: lastSynthHashRef.current,
                },
            }));
        },
    });

    useEffect(() => {
        if (!report || !allSettled || !dataHash) return;
        if (dataHash === storedHash) return; // already analyzed for this data
        if (dataHash === lastSynthHashRef.current) return; // claimed this hash
        if (isSynthesizing) return; // one stream at a time

        lastSynthHashRef.current = dataHash; // claim synchronously to block re-entry
        complete(report.config?.prompt ?? '', {
            body: { widgets: buildSynthesisSummary(entries) },
        });
        // entries/complete captured intentionally; the effect is gated by the
        // primitive dataHash so it can't loop after the analysis is written back.
    }, [report, allSettled, dataHash, storedHash, isSynthesizing]);

    // Cancel an in-flight synthesis the moment we know it's stale: if the data
    // changes (e.g. a widget is added) while a stream is running for the old
    // hash, its analysis is already obsolete, so abort instead of letting it
    // finish and write a doomed result. stop() flips isSynthesizing off without
    // firing onFinish (the SDK skips it on abort), so the gating effect above
    // then starts a fresh stream for the current hash. dataHash is null while
    // data re-settles — we only cancel once the new hash is known and differs.
    useEffect(() => {
        if (!isSynthesizing || !dataHash) return;
        if (dataHash !== lastSynthHashRef.current) stop();
    }, [isSynthesizing, dataHash, stop]);

    // Abort any in-flight synthesis stream on unmount.
    useEffect(() => () => stop(), [stop]);

    // Manual retry after a synthesis error: re-fire for the current data. The
    // hash is already claimed, so the gating effect won't also run.
    const handleRetrySynthesis = () => {
        if (!report || !dataHash) return;
        lastSynthHashRef.current = dataHash;
        complete(report.config?.prompt ?? '', {
            body: { widgets: buildSynthesisSummary(entries) },
        });
    };

    const handleNewReport = () => history.push('/reports');

    // Clone the report server-side (fresh id, version 1, " (copy)" title suffix)
    // and open it. Done entirely on the server so the document isn't
    // round-tripped through the client.
    const handleDuplicateReport = () => {
        if (!report) return;
        duplicateReport.mutate(reportId, {
            onSuccess: (created) => history.push(`/reports/${created.id}/view`),
        });
    };

    // Navigate away immediately rather than in onSuccess: the list cache is
    // updated optimistically (useDeleteReport), so /reports is already correct,
    // and leaving this view at once means the delete's broad reports-invalidation
    // never refetches the now-deleted [reports, id] query (which would throw
    // "not found" and retry with backoff — the source of the long delete pause).
    const handleDeleteReport = () => {
        if (!report) return;
        trackReportDeleted({ reportId, widgetCount: widgets.length });
        deleteReport.mutate(reportId);
        history.push('/reports');
    };

    // Rename only touches config.title (set by the user, distinct from the
    // create-time auto-title); it leaves analysis/dataHash alone so it never
    // triggers re-synthesis. Persists optimistically to the store.
    const handleRenameReport = (title) => {
        if (!report) return;
        save((prev) => ({ ...prev, title }));
        trackReportRenamed({ reportId, widgetCount: widgets.length });
    };

    const config = report?.config;
    const reportTitle = config?.title || config?.prompt || 'Untitled report';
    const hasWidgets = widgets.length > 0;
    // On error, ignore the failed partial stream and show the last-good analysis.
    const analysisText = synthError
        ? config?.analysis?.text
        : streamingAnalysis || config?.analysis?.text;

    return (
        <div>
            <div className="flex items-center justify-between gap-3 px-4 py-3">
                <div className="flex items-center gap-3 min-w-0">
                    <SidebarTrigger className="md:hidden" />
                    <Breadcrumb className="min-w-0">
                        <BreadcrumbList className="flex-nowrap">
                            <BreadcrumbItem>
                                <BreadcrumbLink asChild>
                                    <Link to="/reports">Reports</Link>
                                </BreadcrumbLink>
                            </BreadcrumbItem>
                            {report && (
                                <>
                                    <BreadcrumbSeparator />
                                    <BreadcrumbItem className="min-w-0">
                                        <BreadcrumbPage
                                            className="truncate"
                                            title={reportTitle}
                                        >
                                            {reportTitle}
                                        </BreadcrumbPage>
                                    </BreadcrumbItem>
                                </>
                            )}
                        </BreadcrumbList>
                    </Breadcrumb>
                </div>
                {report && (
                    <div className="flex items-center gap-2 shrink-0">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    aria-label="Report actions"
                                >
                                    <MoreHorizontal />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                    onSelect={() => setIsEditing((v) => !v)}
                                >
                                    {isEditing ? (
                                        <>
                                            <Eye /> Preview mode
                                        </>
                                    ) : (
                                        <>
                                            <Pencil /> Edit mode
                                        </>
                                    )}
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                    onSelect={handleDuplicateReport}
                                >
                                    <Copy /> Duplicate report
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    onSelect={handleDeleteReport}
                                    className="text-destructive focus:text-destructive"
                                >
                                    <Trash2 /> Delete report
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                        <Button
                            variant="ghost"
                            leftIcon={<Presentation />}
                            disabled={!config || widgets.length === 0}
                            onClick={() => setExportOpen(true)}
                        >
                            Export to PowerPoint
                        </Button>
                        <Button
                            variant="outline"
                            leftIcon={<CirclePlus />}
                            onClick={handleNewReport}
                        >
                            New report
                        </Button>
                        <Separator orientation="vertical" className="h-5" />
                        <Button
                            variant="assistant"
                            leftIcon={<SpateAiIcon />}
                            onClick={togglePanel}
                        >
                            Spate AI
                        </Button>
                    </div>
                )}
            </div>
            <PagePadding className="flex flex-col gap-14 max-w-[1696px] pt-6">
                {reportLoading && <Text color="muted">Loading report…</Text>}
                {isError && (
                    <div className="flex flex-col gap-3">
                        <Text color="muted">Report not found.</Text>
                        <Link
                            to="/reports"
                            className="text-sm font-medium text-green-600 hover:underline"
                        >
                            Back to reports
                        </Link>
                    </div>
                )}
                {report && !hasWidgets && !isEditing && (
                    <Text color="muted">This report has no widgets.</Text>
                )}
                {report && (hasWidgets || isEditing) && (
                    <>
                        <ReportSummary
                            title={config?.title || config?.prompt}
                            analysis={analysisText}
                            isLoading={isSynthesizing}
                            error={synthError}
                            onRetry={handleRetrySynthesis}
                            editable={isEditing}
                            onTitleSave={handleRenameReport}
                        />
                        <ReportWidgetGrid
                            onMove={handleMove}
                            slotCount={widgets.length}
                        >
                            {widgets.map((widget, i) => {
                                const q = widgetQueries[i] ?? {};
                                return (
                                    <ReportWidget
                                        key={widget.id}
                                        args={widget.args}
                                        data={q.data}
                                        isLoading={q.isLoading}
                                        isError={q.isError}
                                        error={q.error}
                                        editing={isEditing}
                                        id={widget.id}
                                        index={i}
                                        title={widget.title}
                                        onToggleAggregate={(next) => {
                                            const nextArgs = withListView(
                                                widget.args,
                                                next
                                                    ? 'combined'
                                                    : 'time_series',
                                            );
                                            trackWidgetRenderToggled({
                                                reportId,
                                                args: nextArgs,
                                            });
                                            handleUpdate(widget.id, nextArgs, {
                                                title: widget.title,
                                            });
                                        }}
                                        onExcludeRow={(row) => {
                                            const nextArgs = excludeRowFromArgs(
                                                widget.args,
                                                row,
                                            );
                                            if (!nextArgs) return;
                                            seedWidgetResult(
                                                qc,
                                                nextArgs,
                                                pruneRowFromOutput(q.data, row),
                                            );
                                            trackWidgetRowExcluded({
                                                reportId,
                                                args: nextArgs,
                                            });
                                            handleUpdate(widget.id, nextArgs, {
                                                title: widget.title,
                                            });
                                        }}
                                        onEdit={() => setEditingWidget(widget)}
                                        onDuplicate={() => {
                                            trackWidgetAdded({
                                                reportId,
                                                args: widget.args,
                                                widgetCount: widgets.length + 1,
                                                method: 'duplicate',
                                            });
                                            handleDuplicate(widget.id);
                                        }}
                                        onRemove={() => {
                                            trackWidgetDeleted({
                                                reportId,
                                                args: widget.args,
                                                widgetCount: Math.max(
                                                    0,
                                                    widgets.length - 1,
                                                ),
                                            });
                                            handleRemove(widget.id);
                                        }}
                                        onDropPersist={() => {
                                            handleDropPersist();
                                            trackReportReordered({
                                                reportId,
                                                widgetCount: widgets.length,
                                            });
                                        }}
                                    />
                                );
                            })}
                            {isEditing &&
                                pendingBuilds.map((build) => (
                                    <PendingWidgetBuild
                                        key={build.id}
                                        text={build.text}
                                        onComplete={(built) => {
                                            const total =
                                                widgets.length +
                                                (built?.length ?? 0);
                                            built?.forEach((w) =>
                                                trackWidgetAdded({
                                                    reportId,
                                                    args: w.args,
                                                    widgetCount: total,
                                                    method: 'build',
                                                }),
                                            );
                                            handleAdd(built);
                                            dismissBuild(build.id);
                                        }}
                                        onDismiss={() => dismissBuild(build.id)}
                                    />
                                ))}
                            {isEditing && (
                                <ReportAddWidget
                                    key="__add_widget"
                                    onSubmit={startBuild}
                                />
                            )}
                        </ReportWidgetGrid>
                    </>
                )}
            </PagePadding>
            <SlideshowExportDialog
                open={exportOpen}
                onOpenChange={setExportOpen}
                reportId={reportId}
                report={{
                    meta: { name: config?.title || config?.prompt },
                    config,
                }}
            />
            {editingWidget && (
                <WidgetEditModal
                    key={editingWidget.id}
                    widget={editingWidget}
                    open
                    onOpenChange={(o) => {
                        if (!o) setEditingWidget(null);
                    }}
                    onSave={(newArgs, meta) => {
                        trackWidgetEdited({
                            reportId,
                            args: newArgs,
                            widgetCount: widgets.length,
                            hasTitle: Boolean(meta?.title),
                        });
                        handleUpdate(editingWidget.id, newArgs, meta);
                        setEditingWidget(null);
                    }}
                />
            )}
            <ReportConflictDialog
                open={Boolean(conflict)}
                onReload={reloadFromServer}
                onDismiss={dismissConflict}
            />
        </div>
    );
}

export default ReportView;
